import { useEffect, useState } from 'react';
import { fetchEntityDependents, type EntityRef } from '../api/client.js';
import { resolveEntityTitles } from '../lib/entityTitles.js';

function idsOf(value: unknown): number[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }
  return Object.keys(value as Record<string, unknown>)
    .map((key) => Number(key))
    .filter((id) => Number.isFinite(id))
    .sort((a, b) => a - b);
}

type ChipTone = 'removed' | 'kept' | 'added' | 'dependent';

const TONE_CLASS: Record<ChipTone, string> = {
  removed: 'border-rose-200 bg-rose-50 text-rose-700 line-through',
  kept: 'border-slate-200 bg-slate-50 text-slate-600',
  added: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  dependent: 'border-amber-200 bg-amber-50 text-amber-700',
};

/**
 * Human-readable view of a prerequisite (concept-map edge) change. Resolves entity IDs to
 * titles, drops the meaningless `:1` weight, and groups the diff into Removed / Kept / Added
 * named chips with a net count. When `entityId` is a real existing entity, it also fetches and
 * shows the node's dependents ("Required by") so the AD can see the downstream impact.
 */
export function PrerequisiteDiff({
  courseId,
  entityId,
  before,
  after,
}: {
  courseId?: string | undefined;
  entityId?: number | null | undefined;
  before?: unknown;
  after?: unknown;
}) {
  const hasBefore = before !== undefined;
  const beforeIds = idsOf(before);
  const afterIds = idsOf(after);
  const beforeSet = new Set(beforeIds);
  const afterSet = new Set(afterIds);
  const removed = hasBefore ? beforeIds.filter((id) => !afterSet.has(id)) : [];
  const kept = hasBefore ? beforeIds.filter((id) => afterSet.has(id)) : [];
  const added = hasBefore ? afterIds.filter((id) => !beforeSet.has(id)) : afterIds;
  const prereqIds = [...new Set([...beforeIds, ...afterIds])];

  const [titles, setTitles] = useState<Record<number, string>>({});
  const [dependents, setDependents] = useState<EntityRef[] | null>(null);

  // Resolve prerequisite IDs -> titles (shared session-wide cache).
  const prereqKey = prereqIds.join(',');
  useEffect(() => {
    let cancelled = false;
    if (!courseId || prereqIds.length === 0) {
      return;
    }
    void resolveEntityTitles(courseId, prereqIds).then((resolved) => {
      if (!cancelled) {
        setTitles(resolved);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, prereqKey]);

  // Fetch dependents (only meaningful for an existing entity).
  useEffect(() => {
    let cancelled = false;
    if (!courseId || entityId == null) {
      setDependents(null);
      return;
    }
    void fetchEntityDependents(courseId, entityId)
      .then((refs) => {
        if (!cancelled) {
          setDependents(refs);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDependents(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [courseId, entityId]);

  const labelFor = (id: number) => titles[id] || `Entity ${id}`;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid="prerequisite-diff">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Prerequisites</span>
        {hasBefore ? (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
            {beforeIds.length} → {afterIds.length}
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
            {afterIds.length}
          </span>
        )}
      </div>

      {hasBefore ? (
        <div className="flex flex-col gap-2">
          <ChipGroup heading="Removed" tone="removed" ids={removed} labelFor={labelFor} />
          <ChipGroup heading="Kept" tone="kept" ids={kept} labelFor={labelFor} />
          <ChipGroup heading="Added" tone="added" ids={added} labelFor={labelFor} />
          {removed.length + kept.length + added.length === 0 ? (
            <span className="text-xs text-slate-400">No prerequisite changes.</span>
          ) : null}
        </div>
      ) : (
        <ChipGroup heading="" tone="added" ids={added} labelFor={labelFor} />
      )}

      {entityId != null ? (
        <div className="mt-3 border-t border-slate-100 pt-2">
          <div className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">
            Required by{dependents ? ` (${dependents.length})` : ''}
            <span className="ml-1 font-normal normal-case text-slate-400">— lessons that depend on this node</span>
          </div>
          {dependents === null ? (
            <span className="text-xs text-slate-400">Loading…</span>
          ) : dependents.length === 0 ? (
            <span className="text-xs text-slate-400">Nothing depends on this node yet.</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {dependents.map((dep) => (
                <Chip key={dep.id} id={dep.id} label={dep.title} tone="dependent" />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function ChipGroup({
  heading,
  tone,
  ids,
  labelFor,
}: {
  heading: string;
  tone: ChipTone;
  ids: number[];
  labelFor: (id: number) => string;
}) {
  if (ids.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {heading ? (
        <span className="w-16 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {heading} {ids.length}
        </span>
      ) : null}
      {ids.map((id) => (
        <Chip key={id} id={id} label={labelFor(id)} tone={tone} />
      ))}
    </div>
  );
}

function Chip({ id, label, tone }: { id: number; label: string; tone: ChipTone }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${TONE_CLASS[tone]}`}
      title={`Entity #${id}`}
    >
      {label}
      <span className="font-mono text-[9px] opacity-50">#{id}</span>
    </span>
  );
}
